import type { ReactNode } from 'react';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, ButtonBase, Chip, Stack, Tooltip, Typography } from '@mui/material';

/**
 * A tile's governance badge (design spec §8.1) — a non-interactive `Chip` rendered right of the
 * overline label. Never the tile's only signal: the badge's `label` is always also folded into the
 * tile's `openAriaLabel` by the caller, and its `tooltip` (when present) is supplementary, not the
 * sole way to learn the reason — the drawer the tile opens states it in full.
 */
export interface ProductSummaryTileBadge {
  label: string;
  color: 'default' | 'warning' | 'error';
  /** Hover/focus explanation, e.g. why a submission is blocked. Omit when there is nothing to add. */
  tooltip?: string;
}

/** One "current value" tile on the product card — the launcher of a hidden-detail drawer. */
export interface ProductSummaryTile {
  key: string;
  icon: ReactNode;
  /** Small overline, e.g. "Price" / "Stock". */
  label: string;
  /** The headline current value, e.g. "1 990 ₽" or the primary barcode. */
  value: string;
  /** Secondary line under the value, e.g. "was 2 490 ₽" / "+2 more". Ignored when `captions` is set. */
  caption?: string;
  /**
   * Up to two secondary lines (design spec §8.1) — takes precedence over `caption` when non-empty.
   * Only the price tile uses this today; every other tile keeps the single-line `caption`.
   */
  captions?: string[];
  /** Governance badge next to the overline label (design spec §8.1) — e.g. "Awaiting approval". */
  badge?: ProductSummaryTileBadge;
  /** Render the value in the data monospace (barcodes, SKUs). */
  mono?: boolean;
  /** Warning emphasis (missing price, stock contradiction) — border + value colour, never colour-only (label/caption carry the meaning). */
  warning?: boolean;
  /** Not clickable yet (e.g. a not-yet-saved product has no barcodes sub-resource). */
  disabled?: boolean;
  /** Replaces the caption while disabled, explaining why. */
  disabledHint?: string;
  onOpen: () => void;
  /** Accessible action label, e.g. "Open barcodes". */
  openAriaLabel: string;
}

export interface ProductSummaryTilesProps {
  tiles: ProductSummaryTile[];
}

/**
 * The compact "current data" strip of the product card (adr-merchant-portal-008): one tile per
 * hidden detail block (prices / stock / barcodes / variants), each showing the block's actual
 * headline value and opening its left-anchored `ProductSectionDrawer` on click. Tiles are the
 * card's contract that hiding the blocks loses no information at a glance.
 */
export function ProductSummaryTiles({ tiles }: ProductSummaryTilesProps) {
  if (tiles.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: `repeat(${Math.min(tiles.length, 4)}, minmax(0, 1fr))`,
        },
      }}
    >
      {tiles.map((tile) => (
        <ButtonBase
          key={tile.key}
          focusRipple
          onClick={tile.onOpen}
          disabled={tile.disabled}
          aria-label={tile.openAriaLabel}
          sx={{
            justifyContent: 'stretch',
            textAlign: 'left',
            border: 1,
            borderColor: tile.warning ? 'warning.main' : 'divider',
            borderRadius: 1.5,
            p: 1.5,
            bgcolor: 'background.paper',
            opacity: tile.disabled ? 0.55 : 1,
            transition: (theme) =>
              theme.transitions.create(['border-color', 'background-color'], {
                duration: theme.transitions.duration.shortest,
              }),
            '&:hover': tile.disabled ? undefined : { bgcolor: 'action.hover' },
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
            <Box
              aria-hidden
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: 'action.hover',
                color: tile.warning ? 'warning.main' : 'primary.main',
                '& svg': { fontSize: 20 },
              }}
            >
              {tile.icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.4, letterSpacing: '0.08em' }}
                >
                  {tile.label}
                </Typography>
                {/* Non-interactive — a button inside this tile's own ButtonBase would be invalid
                    (design spec §8.1/§13); the deep link into the reason lives in the drawer this
                    tile opens, and the badge's own label is also folded into the tile's aria-label. */}
                {tile.badge ? (
                  <Tooltip title={tile.badge.tooltip ?? ''} disableHoverListener={!tile.badge.tooltip}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={tile.badge.color}
                      label={tile.badge.label}
                      sx={{ height: 18, fontSize: '0.6875rem', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{
                  fontWeight: 700,
                  color: tile.warning ? 'warning.main' : 'text.primary',
                  fontFamily: tile.mono ? 'JetBrains Mono, monospace' : undefined,
                }}
              >
                {tile.value}
              </Typography>
              {tile.disabled ? (
                tile.disabledHint ? (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {tile.disabledHint}
                  </Typography>
                ) : null
              ) : tile.captions && tile.captions.length > 0 ? (
                tile.captions.slice(0, 2).map((line, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block' }}
                  >
                    {line}
                  </Typography>
                ))
              ) : tile.caption ? (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {tile.caption}
                </Typography>
              ) : null}
            </Box>
            {!tile.disabled ? (
              <ChevronRightRoundedIcon aria-hidden sx={{ color: 'text.disabled', flexShrink: 0 }} />
            ) : null}
          </Stack>
        </ButtonBase>
      ))}
    </Box>
  );
}
